import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  FileText, Calendar, MapPin, User, Building, Car, Hash, 
  Clock, ChevronDown, ChevronUp, ExternalLink, AlertCircle,
  CheckCircle, Eye, Sparkles, Link2, FileSearch
} from 'lucide-react';

// Category icons and colors
const CATEGORY_CONFIG = {
  date: { icon: Calendar, color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Dates' },
  name: { icon: User, color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Names / Aliases' },
  location: { icon: MapPin, color: 'bg-green-100 text-green-700 border-green-200', label: 'Locations' },
  organization: { icon: Building, color: 'bg-orange-100 text-orange-700 border-orange-200', label: 'Organizations' },
  identifier: { icon: Hash, color: 'bg-indigo-100 text-indigo-700 border-indigo-200', label: 'Identifiers' },
  vehicle: { icon: Car, color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Vehicles' },
  circumstance: { icon: FileText, color: 'bg-slate-100 text-slate-700 border-slate-200', label: 'Circumstances' },
  physical: { icon: User, color: 'bg-pink-100 text-pink-700 border-pink-200', label: 'Physical Description' },
  timeline: { icon: Clock, color: 'bg-cyan-100 text-cyan-700 border-cyan-200', label: 'Timeline Events' },
  other: { icon: FileSearch, color: 'bg-gray-100 text-gray-700 border-gray-200', label: 'Other' },
};

// Status badges
const STATUS_CONFIG = {
  approved: { label: 'Verified', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  pending: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  review: { label: 'Under Review', color: 'bg-blue-100 text-blue-700', icon: Eye },
};

export default function ExtractedIntelligence({ items = [], sources = [], isAdmin = false, loading = false }) {
  const [isOpen, setIsOpen] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});

  if (loading) {
    return (
      <Card className="border-2 border-dashed border-amber-300 bg-amber-50/30 dark:bg-amber-950/10">
        <CardContent className="p-6 text-center">
          <Sparkles className="w-6 h-6 animate-pulse text-amber-500 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading extracted intelligence...</p>
        </CardContent>
      </Card>
    );
  }

  // Don't show section if no items
  if (!items || items.length === 0) {
    return null;
  }

  // Group items by category
  const groupedItems = items.reduce((acc, item) => {
    const category = item.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  // Extract timeline events (dates with specific event types)
  const timelineItems = items.filter(item => 
    item.category === 'date' || 
    item.category === 'timeline' ||
    item.field?.toLowerCase().includes('date') ||
    item.field?.toLowerCase().includes('last seen') ||
    item.field?.toLowerCase().includes('found') ||
    item.field?.toLowerCase().includes('identified')
  ).sort((a, b) => {
    // Try to sort by extracted date value
    const dateA = new Date(a.value);
    const dateB = new Date(b.value);
    if (!isNaN(dateA) && !isNaN(dateB)) return dateA - dateB;
    return 0;
  });

  // Count stats
  const totalFacts = items.length;
  const totalSources = sources.length;
  const approvedCount = items.filter(i => i.status === 'approved').length;
  const pendingCount = items.filter(i => i.status === 'pending' || i.status === 'review').length;

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Get source info by ID
  const getSourceInfo = (sourceId) => {
    return sources.find(s => s.id === sourceId);
  };

  return (
    <Card className="border-2 border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50/50 to-orange-50/30 dark:from-amber-950/20 dark:to-orange-950/10">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-amber-100/30 dark:hover:bg-amber-900/20 transition-colors rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg">
                  <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <CardTitle className="text-lg text-amber-900 dark:text-amber-100">
                    Supplemental Case Intelligence
                  </CardTitle>
                  <CardDescription className="text-amber-700/70 dark:text-amber-300/70">
                    AI-extracted facts from secondary sources
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                  {totalFacts} facts
                </Badge>
                {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 space-y-6">
            {/* Disclaimer Banner */}
            <div className="flex items-start gap-3 p-3 bg-amber-100/50 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-800">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Note:</strong> This information was extracted from secondary sources using AI and may require verification. 
                Official case data appears in the sections above.
              </p>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                <div className="text-2xl font-bold text-amber-600">{totalFacts}</div>
                <div className="text-xs text-muted-foreground">Facts Extracted</div>
              </div>
              <div className="text-center p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{totalSources}</div>
                <div className="text-xs text-muted-foreground">Sources</div>
              </div>
              <div className="text-center p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{approvedCount}</div>
                <div className="text-xs text-muted-foreground">Verified</div>
              </div>
              <div className="text-center p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">{timelineItems.length}</div>
                <div className="text-xs text-muted-foreground">Timeline Events</div>
              </div>
            </div>

            {/* Timeline Section */}
            {timelineItems.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-semibold text-amber-900 dark:text-amber-100 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Extracted Timeline
                </h4>
                <div className="relative pl-6 border-l-2 border-amber-300 dark:border-amber-700 space-y-4">
                  {timelineItems.slice(0, 5).map((item, idx) => (
                    <div key={item.id || idx} className="relative">
                      <div className="absolute -left-[25px] w-4 h-4 bg-amber-400 dark:bg-amber-600 rounded-full border-2 border-white dark:border-slate-900" />
                      <div className="bg-white/70 dark:bg-slate-800/70 rounded-lg p-3 shadow-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm text-amber-800 dark:text-amber-200">
                            {item.field || 'Event'}
                          </span>
                          {isAdmin && item.status && (
                            <Badge variant="outline" className={`text-xs ${STATUS_CONFIG[item.status]?.color || ''}`}>
                              {STATUS_CONFIG[item.status]?.label || item.status}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-300">{item.value}</p>
                        {item.source_snippet && (
                          <p className="text-xs text-muted-foreground mt-1 italic">"{item.source_snippet}"</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Separator className="bg-amber-200 dark:bg-amber-800" />

            {/* Grouped Facts by Category */}
            <div className="space-y-3">
              <h4 className="font-semibold text-amber-900 dark:text-amber-100 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Extracted Facts by Category
              </h4>
              
              <div className="space-y-2">
                {Object.entries(groupedItems).map(([category, categoryItems]) => {
                  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other;
                  const Icon = config.icon;
                  const isExpanded = expandedCategories[category] !== false; // Default to expanded
                  
                  return (
                    <div key={category} className="border border-amber-200 dark:border-amber-800 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleCategory(category)}
                        className="w-full flex items-center justify-between p-3 bg-white/50 dark:bg-slate-800/50 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded ${config.color}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <span className="font-medium text-sm">{config.label}</span>
                          <Badge variant="secondary" className="text-xs">
                            {categoryItems.length}
                          </Badge>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      
                      {isExpanded && (
                        <div className="p-3 bg-white/30 dark:bg-slate-900/30 space-y-2">
                          {categoryItems.map((item, idx) => {
                            const source = getSourceInfo(item.source_id);
                            return (
                              <div 
                                key={item.id || idx} 
                                className="flex items-start justify-between gap-3 p-2 bg-white/50 dark:bg-slate-800/50 rounded-md"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                                      {item.field || category}
                                    </span>
                                    {isAdmin && item.status && (
                                      <Badge variant="outline" className={`text-[10px] py-0 ${STATUS_CONFIG[item.status]?.color || ''}`}>
                                        {STATUS_CONFIG[item.status]?.label || item.status}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-800 dark:text-slate-200">{item.value}</p>
                                  {item.source_snippet && (
                                    <p className="text-xs text-muted-foreground mt-1 italic line-clamp-2">
                                      "{item.source_snippet}"
                                    </p>
                                  )}
                                </div>
                                {source && (
                                  <div className="flex-shrink-0">
                                    {source.source_url ? (
                                      <a 
                                        href={source.source_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1"
                                      >
                                        <Link2 className="w-3 h-3" />
                                        Source
                                      </a>
                                    ) : (
                                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                                        <FileText className="w-3 h-3" />
                                        {source.source_type || 'Source'}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sources Section */}
            {sources.length > 0 && (
              <>
                <Separator className="bg-amber-200 dark:bg-amber-800" />
                <div className="space-y-3">
                  <h4 className="font-semibold text-amber-900 dark:text-amber-100 flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Intelligence Sources
                  </h4>
                  <div className="space-y-2">
                    {sources.map((source, idx) => (
                      <div 
                        key={source.id || idx}
                        className="flex items-center justify-between p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg border border-amber-100 dark:border-amber-900"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded">
                            {source.source_type === 'url' ? (
                              <Link2 className="w-4 h-4 text-amber-600" />
                            ) : (
                              <FileText className="w-4 h-4 text-amber-600" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                              {source.title || source.source_url || 'Unknown Source'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {source.items_extracted || 0} items extracted • {source.source_type || 'text'}
                            </p>
                          </div>
                        </div>
                        {source.source_url && (
                          <a 
                            href={source.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-amber-600 hover:text-amber-800"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
